import { mountCompanyFooter } from './ui/company-footer';
import { el } from './dom';
import { resetTutorial } from './tutorial/state';
import { gicon, type IconName } from './ui/icons';
import { soundControlRows } from './audio/audio-controls';
import { playSfx } from './audio/audio';
import {
  renderAccountInfo, renderProfile, renderSecurity, renderTransactions,
} from './settings-account';
import { renderCashout, cashoutOpen } from './settings-cashout';
import { renderCharacterCreator } from './character/creator';
import { saveAppearance } from './character/appearance-store';
import { legalLink } from './ui/legal';

/**
 * Game settings — a tabbed panel opened from the top bar's gear.
 *
 * The toggles still persist to localStorage (one JSON blob) so they survive
 * reloads and can be read by the game views (e.g. the farm reads `autoAttack`).
 * Everything else — profile, security, account information, transactions — is
 * server state, fetched per tab by `settings-account.ts`.
 */
export interface GameSettings {
  immersive: boolean;    // hide the top bar + tab nav for a full-screen play view
  autoAttack: boolean;   // farm: auto-swing at the nearest threat with a blade equipped
  showChat: boolean;     // show the floating world-chat widget
  // NOTE: fog of war is deliberately NOT a setting. It is a mandatory game rule —
  // exploration is core, so the map stays dark until you walk it and there is no
  // player-facing switch. See map/fog.ts and views/farm.ts.
}

const KEY = 'terraboma:settings';
const DEFAULTS: GameSettings = { immersive: false, autoAttack: true, showChat: true };

/**
 * Settings revision, bumped when the stored shape changes in a way that must reach
 * players who already have the old value stored.
 *
 * Stored settings win over defaults (that is the point of storing them), so simply
 * dropping a field from DEFAULTS leaves the old value sitting in localStorage and
 * spreading back over the merge. Rev 3 strips the retired `fogOfWar` key so a player
 * who had once switched fog OFF is not carrying a dead `false` around forever.
 */
const SETTINGS_REV = 3;

/** Keys that were once persisted and must be dropped from stored blobs on load. */
const RETIRED_KEYS = ['fogOfWar'] as const;

export function loadSettings(): GameSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const stored = JSON.parse(raw) as Partial<GameSettings> & { rev?: number };
    for (const k of RETIRED_KEYS) delete (stored as Record<string, unknown>)[k];
    const merged: GameSettings = { ...DEFAULTS, ...stored };
    if ((stored.rev ?? 1) < SETTINGS_REV) saveSettings(merged);
    return merged;
  } catch { return { ...DEFAULTS }; }
}
export function saveSettings(s: GameSettings) {
  try { localStorage.setItem(KEY, JSON.stringify({ ...s, rev: SETTINGS_REV })); } catch { /* private mode */ }
}

export type SettingsTab = 'game' | 'profile' | 'account' | 'security' | 'transactions' | 'cashout';

interface OpenOpts {
  settings: GameSettings;
  onChange: (s: GameSettings) => void;   // fired live on every toggle (already persisted)
  username: string;
  onLogout: () => void;
  /** Stable per-player key for the locally stored hero look (falls back to username). */
  userKey?: string;
  /** Open straight onto a tab — e.g. the HUD's "Transactions" shortcut. */
  tab?: SettingsTab;
}

const TABS: { id: SettingsTab; label: string; icon: IconName }[] = [
  { id: 'game', label: 'Game', icon: 'gear' },
  { id: 'profile', label: 'Profile', icon: 'crown' },
  { id: 'account', label: 'Account', icon: 'check' },
  { id: 'security', label: 'Security', icon: 'shield' },
  { id: 'transactions', label: 'Transactions', icon: 'coin' },
];

export function openSettings(host: HTMLElement, opts: OpenOpts): () => void {
  const s = { ...opts.settings };
  const userKey = opts.userKey ?? opts.username;

  playSfx('ui_open');
  const layer = el('div', 'fixed inset-0 z-[60] flex items-center justify-center bg-black/55 backdrop-blur-[1px] p-2 sm:p-4');
  // Phone-first sizing: nearly full-screen on a small viewport, a comfortable
  // dialog on desktop. The body scrolls; the header and tab strip stay put.
  const panel = el('div',
    'tb-touch-chrome game-font relative flex flex-col w-full max-w-[560px] h-[92vh] sm:h-auto sm:max-h-[86vh] rounded-2xl border border-border bg-surface shadow-lift text-text overflow-hidden');
  layer.appendChild(panel);
  host.appendChild(layer);
  const close = () => { playSfx('ui_close'); layer.remove(); document.removeEventListener('keydown', onKey, true); };
  layer.addEventListener('pointerdown', (e) => { if (e.target === layer) close(); });
  const onKey = (e: KeyboardEvent) => {
    e.stopPropagation();                       // never let settings typing reach the game
    if (e.key === 'Escape') close();
  };
  document.addEventListener('keydown', onKey, true);

  const body = el('div', 'flex-1 min-h-0 overflow-y-auto px-4 py-4');
  const tabStrip = el('div', 'flex gap-1 px-2 overflow-x-auto border-b border-border shrink-0');
  const tabBtns = new Map<SettingsTab, HTMLButtonElement>();

  let active: SettingsTab = opts.tab ?? 'game';

  function setTab(id: SettingsTab) {
    active = id;
    for (const [tid, btn] of tabBtns) {
      btn.className = 'shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-[13px] border-b-2 transition ' +
        (tid === id ? 'border-gold text-gold' : 'border-transparent text-text-dim hover:text-text');
    }
    body.scrollTop = 0;
    if (id === 'game') renderGameTab(body);
    else if (id === 'profile') renderProfile(body, userKey);
    else if (id === 'account') renderAccountInfo(body);
    else if (id === 'security') renderSecurity(body);
    else if (id === 'cashout') renderCashout(body, { onOpenAccount: () => setTab('account') });
    else renderTransactions(body);
  }

  for (const t of TABS) {
    const btn = el('button', '', { onClick: () => setTab(t.id) }, gicon(t.icon, 14), t.label) as HTMLButtonElement;
    tabBtns.set(t.id, btn);
    tabStrip.appendChild(btn);
  }

  /**
   * Cash out is a conditional tab: it appears only once an operator has opened
   * withdrawals. A permanent tab that always says "not available" is a dead end
   * on the one screen where players look for their money, and its absence is the
   * honest answer while the switch is off. A failed check simply leaves it off.
   */
  // Started off a resolved promise so even a synchronous failure in the API layer
  // becomes a rejection here rather than tearing down the whole settings panel
  // on its way out of openSettings().
  Promise.resolve().then(() => cashoutOpen()).then((open) => {
    if (!open || !layer.isConnected || tabBtns.has('cashout')) return;
    const btn = el('button', '', { onClick: () => setTab('cashout') },
      gicon('coin', 14), 'Cash out') as HTMLButtonElement;
    tabBtns.set('cashout', btn);
    tabStrip.appendChild(btn);
    setTab(active);                            // re-apply the active/inactive styling
  }).catch(() => { /* cash-out stays hidden */ });

  // ─── Game tab (the original toggles) ───────────────────────────────────────
  const set = <K extends keyof GameSettings>(k: K, v: GameSettings[K]) => {
    s[k] = v; saveSettings(s); opts.onChange({ ...s });
  };

  /**
   * A settings toggle.
   *
   * The old control was a bare 44x24 pill: under the 44px touch minimum, only the
   * pill itself was tappable (the label was dead space), on and off differed by
   * colour alone, and a keyboard user got no visible focus. It is now one wide
   * switch button — the whole row is the hit area, the state is spelled out in
   * words as well as knob position and colour, and focus draws a gold ring.
   */
  const toggleRow = (label: string, desc: string, key: keyof GameSettings) => {
    const knob = el('span', '');
    const track = el('span', '', {}, knob);
    const word = el('span', '');

    const paint = (on: boolean) => {
      btn.setAttribute('aria-checked', String(on));
      // Squared-off knob + thick border reads as pixel/wood chrome rather than an OS switch.
      track.className = 'relative flex items-center shrink-0 w-14 h-7 px-0.5 rounded-full border-2 transition-colors '
        + (on ? 'bg-gold border-gold/60' : 'bg-surface-2 border-border');
      knob.className = 'block w-5 h-5 rounded-[3px] border transition-transform '
        + (on ? 'bg-bg border-black/40 translate-x-7' : 'bg-text-dim border-border translate-x-0');
      word.className = 'w-8 shrink-0 text-right text-[10px] font-bold tracking-widest '
        + (on ? 'text-gold' : 'text-text-dim');
      word.textContent = on ? 'ON' : 'OFF';
    };

    const btn = el('button', 'w-full min-h-[44px] flex items-center gap-3 py-2.5 text-left border-b border-border/60 '
      + 'outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-surface rounded-sm',
    {
      type: 'button',
      onClick: () => { const nv = !s[key]; set(key, nv as GameSettings[typeof key]); paint(nv); },
    },
    el('span', 'flex-1 min-w-0', {},
      el('span', 'block text-sm', {}, label),
      el('span', 'block text-[11px] text-text-dim', {}, desc)),
    word,
    track) as HTMLButtonElement;
    btn.setAttribute('role', 'switch');
    paint(s[key]);
    return btn;
  };

  const sectionTitle = (text: string) =>
    el('div', 'pt-4 pb-1 text-[11px] uppercase tracking-wider text-text-dim/80', {}, text);

  /** A settings row that opens something, rather than toggling a flag. */
  const actionRow = (label: string, desc: string, action: string, onClick: () => void) =>
    el('div', 'flex items-center justify-between gap-3 py-3 border-b border-border/60', {},
      el('div', 'min-w-0', {},
        el('div', 'text-sm', {}, label),
        el('div', 'text-[11px] text-text-dim', {}, desc)),
      el('button', 'shrink-0 px-3 py-1.5 rounded border border-border text-sm hover:border-gold hover:text-gold',
        { onClick }, action));

  /**
   * The wardrobe the character creator promises ("you can restyle later at the
   * wardrobe"). It IS the creator, re-opened over the game on the same appearance
   * key the world reads. The hero sprite sheets are composed once when a scene is
   * built, so a saved restyle reloads rather than trying to swap them live — the
   * same thing the tutorial Replay row does.
   */
  function openWardrobe() {
    const layer = el('div', 'fixed inset-0 z-[80] bg-bg');
    document.body.appendChild(layer);
    const dispose = renderCharacterCreator(layer, opts.username, (a) => {
      saveAppearance(userKey, a);
      dispose(); layer.remove();
      location.reload();
    });
    // An escape hatch: the creator's own exit is "Confirm", which would rewrite the
    // look even when the player only came to browse.
    layer.appendChild(el('button', 'absolute top-3 right-3 z-10 px-3 py-1.5 rounded border border-border bg-surface text-sm text-text-dim hover:text-text',
      { onClick: () => { dispose(); layer.remove(); } }, 'Cancel'));
  }

  function renderGameTab(host2: HTMLElement) {
    host2.replaceChildren(
      toggleRow('Immersive mode', 'Hide the top bar & tabs while playing', 'immersive'),
      toggleRow('Auto-attack', 'Swing at nearby enemies automatically', 'autoAttack'),
      toggleRow('World chat', 'Show the floating chat widget', 'showChat'),
      actionRow('Tutorial', 'Replay the first-time guided tour', 'Replay',
        () => { resetTutorial(); close(); location.reload(); }),
      actionRow('Wardrobe', 'Restyle your farmer — hero, hair, clothes & colours', 'Open',
        () => { close(); openWardrobe(); }),
      // NOTE: the tool-icon dashboard used to sit here. It is a developer utility
      // (pick which pack sprite stands for each tool), not something a player has
      // any reason to change, so it is no longer exposed from settings.
      // Sound lives in the same tab as the rest of the play settings — it is a play
      // setting, not an account one. The rows write straight through to the audio
      // store (persisted + applied live), so there is nothing to wire into
      // GameSettings here.
      sectionTitle('Sound'),
      ...soundControlRows(),
    );
  }

  // Company/legal details (name, VAT, address…) come from the public company
  // endpoint and are the same block that is printed on invoices. Renders nothing
  // when the endpoint has no data yet.
  //
  // It is legally required, but it is reference material nobody interacts with —
  // and printed in full it ate three or four lines at the bottom of a phone
  // screen, squeezing the settings themselves. So it collapses: one 44px-tall
  // summary row, still one tap from the full block.
  const companyBody = el('div', 'pt-1');
  mountCompanyFooter(companyBody);
  // The Terms and Privacy Policy were reachable only from the register board and
  // the signed-out landing footer. A sibling of companyBody, not a child:
  // mountCompanyFooter clears its host when the company data arrives.
  const legalLinks = el('nav', 'game-font flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-1 text-[12px] text-text-dim',
    { ariaLabel: 'Legal documents' },
    legalLink('terms', 'Terms of Service', 'min-h-[44px] px-1'),
    legalLink('privacy', 'Privacy Policy', 'min-h-[44px] px-1'));
  const companyFooter = el('details', 'px-4 pb-3 shrink-0 border-t border-border/60', {},
    el('summary', 'min-h-[44px] flex items-center justify-center gap-1.5 cursor-pointer select-none text-[11px] text-text-dim hover:text-text outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-sm',
      {}, 'Company & legal details'),
    legalLinks,
    companyBody);

  panel.append(
    el('div', 'flex items-center gap-2 px-4 pt-3.5 pb-2 shrink-0', {},
      el('div', 'flex-1 text-lg font-heading text-gold', {}, 'Settings'),
      el('span', 'text-[11px] text-text-dim truncate max-w-[40%]', {}, opts.username),
      el('button', 'tb-close-x text-text-dim hover:text-text text-xl leading-none px-1', { onClick: close }, '×')),
    tabStrip,
    body,
    // No Sign out here (owner's call). It sat in a permanent footer under every
    // settings tab — a destructive, one-tap action parked beneath panels people
    // open to change a toggle. Signing out belongs with the account, not under the
    // audio sliders.
    companyFooter,
  );

  setTab(active);
  return close;
}
